export const ROLES = ['PLATFORM_ADMIN', 'GYM_OWNER', 'GYM_ADMIN', 'STAFF', 'TRAINER', 'MEMBER', 'RECEPTIONIST', 'ACCOUNTANT', 'MARKETING', 'BARTENDER', 'GYM_MANAGER', 'BRANCH_MANAGER', 'DAY_MANAGER', 'MASSAGE_THERAPIST', 'HR_MANAGER'] as const;
export type Role = (typeof ROLES)[number];

export const GYM_ROLES = ['GYM_OWNER', 'GYM_ADMIN', 'GYM_MANAGER', 'BRANCH_MANAGER', 'DAY_MANAGER', 'STAFF', 'RECEPTIONIST', 'HR_MANAGER', 'ACCOUNTANT', 'MARKETING', 'BARTENDER', 'TRAINER', 'MASSAGE_THERAPIST', 'MEMBER'] as const;
export type GymRole = (typeof GYM_ROLES)[number];

export const STAFF_ROLES = ['GYM_OWNER', 'GYM_ADMIN', 'GYM_MANAGER', 'BRANCH_MANAGER', 'DAY_MANAGER', 'STAFF', 'RECEPTIONIST', 'HR_MANAGER', 'ACCOUNTANT', 'MARKETING', 'BARTENDER', 'TRAINER', 'MASSAGE_THERAPIST'] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];

/** Roles that work at every branch and are never assigned to one. */
export const WORKS_EVERYWHERE_ROLES = ['GYM_OWNER', 'GYM_ADMIN', 'GYM_MANAGER', 'HR_MANAGER'] as const satisfies readonly StaffRole[];

export function worksEverywhere(role: string): boolean {
  return (WORKS_EVERYWHERE_ROLES as readonly string[]).includes(role);
}

/** Roles only an owner or a gym admin may grant, change or take away. */
export const OWNER_GRANTED_ROLES = ['GYM_OWNER', 'GYM_MANAGER', 'HR_MANAGER'] as const satisfies readonly StaffRole[];

/** The roles a gym admin, or Bulk acting in a gym, can switch the admin into to see and work it as that role does. */
export const VIEW_AS_ROLES = ['GYM_OWNER', 'GYM_MANAGER', 'BRANCH_MANAGER', 'DAY_MANAGER', 'RECEPTIONIST', 'STAFF', 'HR_MANAGER', 'ACCOUNTANT', 'MARKETING', 'BARTENDER', 'TRAINER', 'MASSAGE_THERAPIST'] as const satisfies readonly StaffRole[];
export type ViewAsRole = (typeof VIEW_AS_ROLES)[number];

export function isViewAsRole(role: string): role is ViewAsRole {
  return (VIEW_AS_ROLES as readonly string[]).includes(role);
}

/** The accounts a branch manager manages: the roles below them that work at a branch. */
export const BRANCH_MANAGER_GRANTS = ['DAY_MANAGER', 'STAFF', 'RECEPTIONIST', 'BARTENDER', 'TRAINER', 'MASSAGE_THERAPIST'] as const satisfies readonly StaffRole[];

/** The accounts an HR manager manages: every staff role but those who run the gym, and HR itself. */
export const HR_GRANTS = ['BRANCH_MANAGER', 'DAY_MANAGER', 'STAFF', 'RECEPTIONIST', 'ACCOUNTANT', 'MARKETING', 'BARTENDER', 'TRAINER', 'MASSAGE_THERAPIST'] as const satisfies readonly StaffRole[];

/** The roles a gym can switch optional abilities for. Owners, admins and gym managers always hold every ability. */
export const SWITCHABLE_ROLES = ['MARKETING', 'BRANCH_MANAGER', 'DAY_MANAGER', 'RECEPTIONIST', 'STAFF', 'BARTENDER', 'TRAINER', 'MASSAGE_THERAPIST', 'HR_MANAGER', 'ACCOUNTANT'] as const satisfies readonly StaffRole[];
export type SwitchableRole = (typeof SWITCHABLE_ROLES)[number];

export const ABILITY_KEYS = [
  'menu.edit',
  'website.publish',
  'legal.publish',
  'campaigns.run',
  'campaigns.sms',
  'campaigns.email',
  'staff.manage',
  'payments.refund',
  'wallet.correct',
  'refunds.request',
  'wallet.top_up',
  'bar.sell',
  'bar.refund',
  'bar.stock',
  'classes.edit',
  'finance.close_month',
  'expenses.drawer',
  'trainers.roster',
  'trainers.website',
  'schedules.manage',
  'leave.decide',
  'plans.edit',
  'appointments.book_for_member',
  'tags.manage',
] as const;
export type AbilityKey = (typeof ABILITY_KEYS)[number];

/** The roles whose permissions the gym admin sets one by one. The gym admin holds everything and is never on it. */
export const PERMISSION_MATRIX_ROLES = ['GYM_OWNER', 'GYM_MANAGER', 'BRANCH_MANAGER', 'DAY_MANAGER', 'RECEPTIONIST', 'STAFF', 'HR_MANAGER', 'ACCOUNTANT', 'MARKETING', 'BARTENDER', 'TRAINER', 'MASSAGE_THERAPIST'] as const satisfies readonly StaffRole[];
export type PermissionMatrixRole = (typeof PERMISSION_MATRIX_ROLES)[number];

export function isPermissionMatrixRole(role: string): role is PermissionMatrixRole {
  return (PERMISSION_MATRIX_ROLES as readonly string[]).includes(role);
}

export const PERMISSION_RISKS = ['normal', 'sensitive', 'money'] as const;
export type PermissionRisk = (typeof PERMISSION_RISKS)[number];

export const PERMISSION_GRANTS = ['staff', 'member', 'admin-only'] as const;
export type PermissionGrant = (typeof PERMISSION_GRANTS)[number];

/** Why a role holds a permission or not: its default, an ability switched away from its default, or the gym admin's own tick. */
export const PERMISSION_SOURCES = ['DEFAULT', 'ABILITY', 'OVERRIDE'] as const;
export type PermissionSource = (typeof PERMISSION_SOURCES)[number];

/**
 * The groups the permission catalogue is shown in, in page order. A permission a gym module grants sits under that
 * module's key; the rest sit under groups of their own that no module switches off.
 */
export const PERMISSION_MODULES = [
  'gym',
  'staff',
  'members',
  'memberships',
  'payments',
  'desk',
  'classes',
  'trainers',
  'appointments',
  'access',
  'menu',
  'bar',
  'campaigns',
  'leads',
  'targets',
  'corporate',
  'website',
  'finance',
  'reports',
  'team',
  'payroll',
  'equipment',
  'tasks',
  'knowledge',
  'recognition',
  'recruiting',
  'practice',
] as const;
export type PermissionModule = (typeof PERMISSION_MODULES)[number];

/**
 * Where a person signs in. A gym has three addresses: its own domain for members (SITE), `service.` in front of it for
 * the staff who run the floor, and `admin.` for those who run the gym. Bulk's own address (PLATFORM) is for platform admins.
 */
export const PORTALS = ['PLATFORM', 'SITE', 'SERVICE', 'ADMIN'] as const;
export type Portal = (typeof PORTALS)[number];

export const PORTAL_PREFIXES = { SERVICE: 'service', ADMIN: 'admin' } as const satisfies Partial<Record<Portal, string>>;

export const ADMIN_PORTAL_ROLES = ['GYM_OWNER', 'GYM_ADMIN', 'GYM_MANAGER', 'BRANCH_MANAGER', 'HR_MANAGER'] as const satisfies readonly StaffRole[];
export const SERVICE_PORTAL_ROLES = ['DAY_MANAGER', 'STAFF', 'RECEPTIONIST', 'ACCOUNTANT', 'MARKETING', 'BARTENDER', 'TRAINER', 'MASSAGE_THERAPIST'] as const satisfies readonly StaffRole[];

/** The address a gym role signs in at. */
export function portalForRole(role: Role): Exclude<Portal, 'PLATFORM'> {
  if (role === 'PLATFORM_ADMIN' || (ADMIN_PORTAL_ROLES as readonly string[]).includes(role)) return 'ADMIN';
  if ((SERVICE_PORTAL_ROLES as readonly string[]).includes(role)) return 'SERVICE';
  return 'SITE';
}

export const PERMISSIONS = [
  'gym.read',
  'gym.update',
  'locations.manage',
  'rooms.manage',
  'members.read',
  'members.read_own',
  'members.create',
  'members.update',
  'members.delete',
  'members.tags',
  'members.export',
  'tags.manage',
  'segments.manage',
  'memberships.read',
  'memberships.create',
  'memberships.update',
  'memberships.pause',
  'memberships.resume',
  'memberships.comp',
  'memberships.delete',
  'plans.manage',
  'trainers.read',
  'trainers.manage',
  'trainers.profile',
  'classes.read',
  'classes.create',
  'classes.update',
  'classes.delete',
  'classes.own',
  'appointments.read',
  'appointments.manage',
  'appointments.own',
  'appointments.book_for_member',
  'appointments.book',
  'bookings.read',
  'bookings.create',
  'bookings.cancel',
  'payments.read',
  'payments.create',
  'payments.record',
  'payments.refund_request',
  'payments.refund',
  'wallet.read',
  'wallet.top_up',
  'wallet.correct',
  'access.read',
  'access.open',
  'access.manage',
  'campaigns.read',
  'campaigns.manage',
  'campaigns.sms',
  'campaigns.email',
  'leads.read',
  'leads.manage',
  'equipment.read',
  'equipment.manage',
  'maintenance.report',
  'maintenance.manage',
  'tasks.read',
  'tasks.manage',
  'tasks.complete',
  'recognition.manage',
  'kudos.send',
  'targets.read',
  'targets.manage',
  'corporate.read',
  'corporate.manage',
  'kb.read',
  'kb.manage',
  'recruiting.read',
  'recruiting.manage',
  'recruiting.interview',
  'menu.read',
  'menu.manage',
  'bar.sell',
  'bar.refund',
  'bar.stock',
  'bar.stock.read',
  'bar.insights',
  'purchasing.read',
  'purchasing.receive',
  'purchasing.manage',
  'website.read',
  'website.manage',
  'website.publish',
  'content.read',
  'content.manage',
  'content.publish',
  'finance.read',
  'finance.export',
  'finance.manage',
  'finance.month.close',
  'finance.month.reopen',
  'finance.branch',
  'expenses.read',
  'expenses.manage',
  'expenses.cash',
  'practice.manage',
  'reception.use',
  'integrations.read',
  'integrations.manage',
  'reports.read',
  'reports.manage',
  'audit.read',
  'users.manage',
  'roles.configure',
  'network.manage',
  'employees.read',
  'employees.manage',
  'employees.pay.read',
  'employees.pay.manage',
  'leave.request',
  'leave.read',
  'leave.approve',
  'leave.manage',
  'timesheets.own',
  'timesheets.clock',
  'timesheets.manage',
  'shifts.own',
  'shifts.manage',
  'onboarding.own',
  'onboarding.manage',
  'reviews.own',
  'reviews.manage',
  'payroll.read',
  'payroll.manage',
  'payroll.approve',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/**
 * Where a staff request comes from, measured against the gym's allowed networks. UNRESTRICTED: the gym has none, or the
 * role is never limited (owners, admins, gym managers, members). OFF_SITE requests keep only reading and team self-service.
 */
export const NETWORK_STATUSES = ['UNRESTRICTED', 'ON_SITE', 'OFF_SITE'] as const;
export type NetworkStatus = (typeof NETWORK_STATUSES)[number];

/**
 * Whether a gym holds the people who work in shifts to their shifts. OFF: nobody is. OFF_DUTY: outside a shift they
 * keep their own team self-service. SIGN_OUT: they keep only the clock and their own shifts, and the app signs them out.
 */
export const SHIFT_ACCESS_MODES = ['OFF', 'OFF_DUTY', 'SIGN_OUT'] as const;
export type ShiftAccessMode = (typeof SHIFT_ACCESS_MODES)[number];

/** The roles that work in shifts. The gym admin may leave some of them out; no other role can be held to a shift. */
export const SHIFT_ROLES = ['RECEPTIONIST', 'STAFF', 'TRAINER', 'MASSAGE_THERAPIST', 'BARTENDER', 'DAY_MANAGER'] as const satisfies readonly StaffRole[];
export type ShiftRole = (typeof SHIFT_ROLES)[number];

export function isShiftRole(role: string): role is ShiftRole {
  return (SHIFT_ROLES as readonly string[]).includes(role);
}

export const DUTY_STATES = ['ON', 'OFF'] as const;
export type DutyState = (typeof DUTY_STATES)[number];

/** What keeps someone on duty: a rostered shift (with its grace minutes), being clocked in, or a manager's extension. */
export const DUTY_BASES = ['ROSTER', 'CLOCK', 'EXTENSION'] as const;
export type DutyBasis = (typeof DUTY_BASES)[number];
